import { type ConfiguratorConditionGroup, type ConfiguratorOption as ConfiguratorOption_, type ConfiguratorOptionGroup, type ConfiguratorPriceTable, ConfiguratorPriceTableConfig } from "#models/Configurator"
import type { OptionConditionInput } from "#resources/Admin/Configurator/ConditionGroup"
import type * as ConfiguratorRsc from "#resources/Admin/Configurator/Configurator"
import { ConfiguratorOptionInput } from "#resources/Admin/Configurator/Option"
import { ConfiguratorOptionGroupInput } from "#resources/Admin/Configurator/OptionGroup"
import { ConfiguratorPriceTableItemInput } from "#resources/Admin/Configurator/PriceTable"
import type { ConfiguratorOptionEntryView, ConfiguratorOptionGroupEntryView } from "#resources/Admin/Configurator/Views"
import type { ActionType, Condition, ConditionAction, ConditionGroup, ConditionRule, ConfiguratorOption, ConfiguratorWithCategory, MiniOption, OptionGroup, PriceTable, RuleComparisonOperator, RuleValidationMode } from "@/internal/interfaces"
import { decodeUnknownSync, NonEmptyString255 } from "effect-app/Schema"
import type { Mutable } from "effect-app/Types"

export function mapOption(optionGroup: ConfiguratorOptionGroupEntryView) {
  return (o_: ConfiguratorOption_): ConfiguratorOption => ({
    ...mapMiniOption(o_),
    optionGroup
  })
}

export function mapMiniOption(o_: ConfiguratorOption_): MiniOption {
  return o_._tag === "list"
    ? {
      ...o_,
      items: o_.items.map((_) => ({
        ..._,
        category: _.categoryId
          ? (o_.categories.find((c) => c.id === _.categoryId) ?? null)
          : null
      }))
    }
    : o_
}

export function mapOptionGroup(g: ConfiguratorOptionGroup): OptionGroup {
  const optionGroup: Mutable<OptionGroup> = {
    ...g,
    options: [] as ConfiguratorOption[]
  }

  const options = g.options.map(mapOption(g))
  optionGroup.options = options
  return optionGroup
}

export function mapPriceTable(
  p: ConfiguratorPriceTable,
  options: readonly ConfiguratorOptionEntryView[]
): PriceTable {
  return {
    ...p,
    columnUseClosestValue: p.columns?.useClosestValue || null,
    rowUseClosestValue: p.rows?.useClosestValue || null,
    columnOption: p.columns?.optionId
      ? options.find((o) => o.id === p.columns?.optionId)!
      : null,
    rowOption: p.rows?.optionId
      ? options.find((o) => o.id === p.rows?.optionId)!
      : null,
    hasColumns: !!p.columns,
    hasRows: !!p.rows
  }
}

// Maps one persisted or server-produced condition to the editor shape.
export function mapCondition(options: readonly MiniOption[]) {
  return (c: OptionConditionInput): Condition => ({
    ...c,
    ruleValidationMode: c.ruleValidationMode as RuleValidationMode,
    rules: c.rules.map(
      (r) =>
        ({
          id: r.id,
          targetKind: r.groupId !== null ? "group" : "option",
          option: r.optionId ? options.find((_) => _.id === r.optionId) ?? null : null,
          comparisonValue: r.comparisonValue,
          comparisonOperator: r.comparisonOperator as RuleComparisonOperator
        }) satisfies ConditionRule
    ),
    actions: c.actions.map(
      (a) =>
        ({
          ...a,
          option: options.find((_) => _.id === a.optionId) ?? null,
          type: a.type as ActionType
        }) satisfies ConditionAction
    )
  })
}

export function mapConditionGroup(options: readonly MiniOption[]) {
  return (g: ConfiguratorConditionGroup): ConditionGroup => ({
    ...g,
    conditions: g.conditions.map(mapCondition(options))
  })
}

// TODO: parseEUnsafe would be better, but we need more work.
const parseOption = decodeUnknownSync(ConfiguratorOptionInput)
export function toOptionInput(option: ConfiguratorOption.Encoded) {
  const o = {
    ...option,
    ...(option._tag === "list"
      ? {
        categories: option.categories.map((_) => ({
          ...emptyStringsToNull(_) // TODO: convert within forms?
        })),
        items: option.items.map((_) => ({
          ...emptyStringsToNull(_),
          categoryId: _.category?.id ?? null
        }))
      }
      : {})
  }
  return parseOption(emptyStringsToNull(o))
}

function emptyStringsToNull(o: object) {
  return Object.entries(o).reduce(
    (acc, [k, v]) => {
      // we prefer null over empty strings
      acc[k] = v === "" ? null : v
      return acc
    },
    {} as Record<string, unknown>
  )
}

export const parseOptionGroup = S.decodeSync(ConfiguratorOptionGroupInput)

const parsePriceTableItem = decodeUnknownSync(ConfiguratorPriceTableItemInput)

/*
 * Converts a PriceTable UI object to the format expected by the API.
 * Filters out "ghost" items that don't match the current row/column configuration:
 * - If columns are disabled, removes items with non-null column values
 * - If rows are disabled, removes items with non-null row values
 * This ensures backwards compatibility and cleans up stale data on save.
 */
export function createPriceTableObj(priceTable: PriceTable) {
  const filteredItems = priceTable.items.filter((item) => {
    if (!priceTable.hasColumns && item.column !== null) return false
    if (!priceTable.hasRows && item.row !== null) return false
    return true
  })

  const result = {
    // TODO: parse, as option might be null, but must not be on the server
    columns: priceTable.columnOption
      ? ConfiguratorPriceTableConfig.make({
        optionId: priceTable.columnOption.id,
        useClosestValue: priceTable.columnUseClosestValue
      })
      : null,
    rows: priceTable.rowOption
      ? ConfiguratorPriceTableConfig.make({
        optionId: priceTable.rowOption.id,
        useClosestValue: priceTable.rowUseClosestValue
      })
      : null,
    items: filteredItems.map((_) => parsePriceTableItem(_)),
    name: NonEmptyString255(priceTable.name)
  }
  return result
}

export function convertToConfiguratorWithCategory(
  configuretorsList: ConfiguratorRsc.AllConfiguratorsResponse
) {
  return [...configuretorsList.items].map(
    (configurator): ConfiguratorWithCategory => ({
      ...configurator,
      category: [...configuretorsList.categories].find(
        (category) => category.id === configurator.categoryId
      ) ?? null
    })
  )
}
